/* RESPONSIBLE TEAM: team-standalone */

import { inject as service } from '@ember/service';
import Helper from '@ember/component/helper';
import type Session from 'embercom/services/session';
import { ANSWER_BOT_ESSENTIAL_ID, ANSWER_BOT_ID } from 'embercom/lib/billing';
import Workspace from 'embercom/objects/workspace';

type CanSeeCustomAnswersSignature = {
  Args: {};
  Return: boolean;
};

export function canSeeCustomAnswers(app?: any, customerService?: any, session?: any) {
  // For Inbox 2, we don't have the app service. Instead, we have a
  // lightweight workspace object on the session service which we can query
  // to figure out if a feature is enabled.
  if (session && !session.isWorkspaceLoaded) {
    return false;
  }
  // we don't want to allow migrated customers to create custom answers
  if (app?.isMigratingFromStandaloneToSuite) {
    return false;
  }
  // if the app is a standalone app, and team-standalone-custom-answers is not enabled, return false
  if (app?.canUseStandalone && !app.canUseFeature('team-standalone-custom-answers')) {
    return false;
  }
  let workspaceObj = app instanceof Workspace ? app : session?.workspace;
  let isFeatureEnabled =
    app && !(app instanceof Workspace)
      ? app.canUseFeature.bind(app)
      : workspaceObj.isFeatureEnabled.bind(workspaceObj);
  let products = customerService?.products || (!(app instanceof Workspace) && app?.products) || [];
  if (!app && !session?.isWorkspaceLoaded) {
    // @ts-expect-error
    return window?.__embercom_boot_data?.is_fin_standalone_app || false;
  }
  let saCustomAnswers = isFeatureEnabled('team-standalone-custom-answers');
  let forceShowCustomAnswers = isFeatureEnabled('enable-legacy-custom-answers');
  let suggestionSettingsEnabled = isFeatureEnabled('show-fin-suggestion-settings-to-p5-customers');
  let hasAnswerBotEssentialPlanActive =
    (customerService?.activePlans || []).filter(
      (plan: any) => `${plan.id}` === ANSWER_BOT_ESSENTIAL_ID,
    ).length > 0;
  let hasCustomAnswersAddonActive =
    products.filter((product: any) => product.active && `${product.id}` === ANSWER_BOT_ID).length >
    0;

  return (
    forceShowCustomAnswers ||
    saCustomAnswers ||
    hasCustomAnswersAddonActive ||
    hasAnswerBotEssentialPlanActive ||
    suggestionSettingsEnabled
  );
}

export default class CanSeeCustomAnswers extends Helper<CanSeeCustomAnswersSignature> {
  @service declare appService: { app: any } | null;
  @service declare customerService: any | null;
  @service declare session: Session | null;

  compute() {
    this.session?.switchWorkspace(this.appService?.app?.id);
    return canSeeCustomAnswers(this.appService?.app, this.customerService, this.session);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'can-see-custom-answers': typeof CanSeeCustomAnswers;
  }
}
